// ─── useAttachments — per-brand attachment index + upload/delete helpers ────
// Shared by every <AttachmentButton/> on every row. Fetches the full list of
// attachments for the current brand once per session (refreshed opportunisti-
// cally), indexes them by `${entityType}:${entityId}` for O(1) lookups, and
// exposes upload/delete helpers that (a) talk to Supabase Storage directly
// with the user's session token, then (b) record the metadata via
// /api/attachments so audits get written.
//
// Shape of each attachment:
//   { id, entity_type, entity_id, file_name, file_size, mime_type,
//     uploaded_by, uploaded_at }

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./useData.js";

// Module-level cache keyed by `${accessToken}:${brandKey}`. Multiple hook
// instances share the same fetched list.
let _cache = { key: null, rows: null, brandId: null, accountId: null, planTier: "starter", canAttach: false, fetchedAt: 0 };
const REFRESH_MS = 60_000;

function cacheKey(token, brandKey) { return `${token || ""}:${brandKey || "main"}`; }

export const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png"];
export const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

/** Make a stable entity key for the index Map. */
export function entityKey(entityType, entityId) {
  return `${entityType}:${String(entityId)}`;
}

function uuid() {
  // Good-enough UUID-v4 without pulling in a lib.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function extFromMime(mime) {
  if (mime === "application/pdf") return "pdf";
  if (mime === "image/jpeg") return "jpg";
  if (mime === "image/png") return "png";
  return "bin";
}

export function useAttachments({ brandKey = "main" } = {}) {
  const [rows, setRows] = useState(() => _cache.rows || []);
  const [brandId, setBrandId] = useState(_cache.brandId);
  const [accountId, setAccountId] = useState(_cache.accountId);
  const [planTier, setPlanTier] = useState(_cache.planTier || "starter");
  const [canAttach, setCanAttach] = useState(!!_cache.canAttach);
  const [loading, setLoading] = useState(!_cache.rows);
  const [error, setError] = useState("");
  const inflight = useRef(false);

  const refresh = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) { setLoading(false); return; }
      const res = await fetch("/api/attachments", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
        body: JSON.stringify({ action: "list", brandKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to load attachments");
      _cache = {
        key: cacheKey(s.access_token, brandKey),
        rows: data.attachments || [],
        brandId: data.brandId || null,
        accountId: null, // populated on-demand via resolve-brand
        planTier: data.planTier || "starter",
        canAttach: !!data.canAttach,
        fetchedAt: Date.now(),
      };
      setRows(_cache.rows);
      setBrandId(_cache.brandId);
      setPlanTier(_cache.planTier);
      setCanAttach(_cache.canAttach);
      setError("");
    } catch (e) {
      setError(e?.message || "failed");
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, [brandKey]);

  useEffect(() => {
    const stale = !_cache.rows || (Date.now() - _cache.fetchedAt > REFRESH_MS);
    if (stale) refresh();
  }, [refresh]);

  // Index: entityKey → attachment row
  const byEntity = useMemo(() => {
    const map = new Map();
    for (const r of rows) map.set(entityKey(r.entity_type, r.entity_id), r);
    return map;
  }, [rows]);

  /** Upload a file for a given entity. Replaces any existing attachment. */
  const upload = useCallback(async ({ file, entityType, entityId }) => {
    if (!file) throw new Error("No file selected");
    if (!ALLOWED_MIME.includes(file.type)) {
      throw new Error("Unsupported file type — PDF, JPG, or PNG only");
    }
    if (file.size > MAX_SIZE) {
      throw new Error("File too large (max 5 MB)");
    }
    const { data: { session: s } } = await supabase.auth.getSession();
    if (!s?.access_token) throw new Error("Not signed in");

    // Need accountId + brandId to form the storage path. Use cached values
    // if present, else ask the API to resolve them.
    let acctId = accountId;
    let brId = brandId;
    if (!acctId || !brId) {
      const res = await fetch("/api/attachments", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
        body: JSON.stringify({ action: "resolve-brand", brandKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to resolve brand");
      acctId = data.accountId;
      brId = data.brandId;
      setAccountId(acctId); setBrandId(brId);
    }

    const ext = extFromMime(file.type);
    const storagePath = `${acctId}/${brId}/${entityType}/${entityId}/${uuid()}.${ext}`;

    // Upload straight to Supabase Storage using the user's JWT — RLS in
    // migration 007 scopes writes to the caller's account prefix.
    const { error: upErr } = await supabase.storage
      .from("receipts")
      .upload(storagePath, file, {
        contentType: file.type,
        upsert: false, // fresh uuid every time; replace handled server-side
        cacheControl: "3600",
      });
    if (upErr) throw new Error(upErr.message || "Upload failed");

    // Record metadata + audit via the API (service-role insert).
    const recRes = await fetch("/api/attachments", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
      body: JSON.stringify({
        action: "record", brandKey, entityType, entityId: String(entityId),
        fileName: file.name, fileSize: file.size, mimeType: file.type,
        storagePath,
      }),
    });
    const recData = await recRes.json();
    if (!recRes.ok) {
      // Best-effort cleanup of the orphaned storage object
      try { await supabase.storage.from("receipts").remove([storagePath]); } catch {}
      throw new Error(recData?.error || "Failed to record attachment");
    }
    // Invalidate cache so next render reflects the new row
    _cache.fetchedAt = 0;
    await refresh();
    return recData.id;
  }, [accountId, brandId, brandKey, refresh]);

  /** Open an attachment in a new tab via a 1-hour signed URL. */
  const openInNewTab = useCallback(async (attachmentId) => {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (!s?.access_token) throw new Error("Not signed in");
    const res = await fetch("/api/attachments", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
      body: JSON.stringify({ action: "signed-url", attachmentId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || "Failed to open");
    window.open(data.url, "_blank", "noopener,noreferrer");
  }, []);

  /** Delete an attachment (storage + row). */
  const remove = useCallback(async (attachmentId) => {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (!s?.access_token) throw new Error("Not signed in");
    const res = await fetch("/api/attachments", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.access_token}` },
      body: JSON.stringify({ action: "delete", attachmentId }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || "Failed to delete");
    }
    _cache.fetchedAt = 0;
    await refresh();
  }, [refresh]);

  return { rows, byEntity, loading, error, refresh, upload, openInNewTab, remove, planTier, canAttach };
}
